import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { BaseService } from './base-service';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root',
})
export class DownloadService extends BaseService {
    isEnergyReportLoading$ = new BehaviorSubject<boolean>(false);
    private readonly ENERGY_REPORT_URL = 'https://ews.n2g-iona.net/energy-report?period=';

    constructor(
        protected http: HttpClient,
        private translate: TranslateService,
        private apiService: ApiService,
        protected auth: ApiService,
        protected user: UserService
    ) {
        super(http, auth, user);
    }

    /** Open energy report */
    openEnergyReport(month: string) {
        this.isEnergyReportLoading$.next(true);
        const token = this.apiService.getToken();

        if (!token) {
            this.isEnergyReportLoading$.next(false);
            return;
        }

        const timestamp = Date.now();
        const labelpartner = this.user.getActiveUserProvider();

        const url = `${this.ENERGY_REPORT_URL}${month}&token=${encodeURIComponent(token)}&labelpartner=${encodeURIComponent(labelpartner)}&ts=${timestamp}`;
        window.open(url, '_blank');

        this.isEnergyReportLoading$.next(false);
    }

    getTranslatedMonths(): Observable<Array<{ value: string; label: string }>> {
        const currentDate = new Date();

        return this.translate.get('screens.dashboard.months').pipe(
            map((monthsList: string[]) => {
                const availableMonths: Array<{ value: string; label: string }> = [];

                for (let i = 0; i <= 3; i++) {
                    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
                    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    const monthName = monthsList[date.getMonth()];
                    const label = `${monthName} ${date.getFullYear()}`;
                    availableMonths.push({ value, label });
                }

                return availableMonths;
            })
        );
    }

    exportConsumptionData(selectedMonth: string): Observable<Blob> {
        const [year, month] = selectedMonth.split('-');
        const fromDate = `${year}-${month}-01`;
        const lastDay = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
        const toDate = `${year}-${month}-${lastDay}`;

        const exportUrl = `${environment.urls.userGroup}/data-export/consumption/electricity/quarter-hours/${fromDate}/${toDate}/export?output=csv`;

        const token = this.apiService.getToken();
        if (!token) {
            return of(null as unknown as Blob);
        }

        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.get(exportUrl, { headers, responseType: 'blob' });
    }

    exportToThirdParties(): Observable<any> {
        const token = this.apiService.getToken();
        if (!token) {
            return of(null);
        }

        const exportUrl = `${this.API_BASE_URL}export?export_reason=EU_DATA_ACT_EXPORT`;
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.get(exportUrl, { headers });
    }

    exportGdprData(): Observable<any> {
        const token = this.apiService.getToken();
        if (!token) {
            return of(null);
        }

        const exportUrl = `${this.API_BASE_URL}export?export_reason=GDPR_EXPORT`;
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.get(exportUrl, { headers });
    }
}
